/**
 * Attachment module schemas (E26 / Patch 1).
 *
 * Covers documents and communications.
 *
 * In this patch the anchor is project-tree only (project_id required, optional
 * sub-anchor). Patch E27 will extend with master-entity anchors (client,
 * supplier, master department) and threading columns (thread_id,
 * in_reply_to_id).
 */

import { Type, type Static } from '@sinclair/typebox';
import {
  AttachmentKinds,
  CommDirections,
  CommTypes,
  DocumentTypes,
} from '../enums.js';
import {
  IsoDateSchema,
  IsoDateTimeSchema,
  RowVersionSchema,
  UuidSchema,
} from './common.js';

// ---------------------------------------------------------------------------
// Anchor (project-tree only in this patch)
// ---------------------------------------------------------------------------

/**
 * Anchor — exactly one of:
 *   • Project-tree: projectId set, optionally narrowed to ONE sub-anchor
 *   • Master entity: ONE of clientId / supplierId / masterDepartmentId set,
 *     all project-tree fields null
 *
 * The mutex is enforced server-side (lib/attachments-validate.ts) and as a
 * CHECK constraint at the DB level. Passing both kinds or zero anchors results
 * in a 400.
 */
export const AttachmentAnchorSchema = Type.Object({
  // Project-tree
  projectId: Type.Optional(Type.Union([UuidSchema, Type.Null()])),
  projectItemId: Type.Optional(Type.Union([UuidSchema, Type.Null()])),
  orderBatchId: Type.Optional(Type.Union([UuidSchema, Type.Null()])),
  bankGuaranteeId: Type.Optional(Type.Union([UuidSchema, Type.Null()])),
  inspectionEventId: Type.Optional(Type.Union([UuidSchema, Type.Null()])),
  // Master entity
  clientId: Type.Optional(Type.Union([UuidSchema, Type.Null()])),
  supplierId: Type.Optional(Type.Union([UuidSchema, Type.Null()])),
  masterDepartmentId: Type.Optional(Type.Union([UuidSchema, Type.Null()])),
});
export type AttachmentAnchor = Static<typeof AttachmentAnchorSchema>;

// ---------------------------------------------------------------------------
// Read-side response
// ---------------------------------------------------------------------------

const DocumentTypeSchema = Type.Union(DocumentTypes.map((t) => Type.Literal(t)));
const CommTypeSchema = Type.Union(CommTypes.map((t) => Type.Literal(t)));
const CommDirectionSchema = Type.Union(CommDirections.map((d) => Type.Literal(d)));
const AttachmentKindSchema = Type.Union(AttachmentKinds.map((k) => Type.Literal(k)));

export const CommunicationDepartmentRefSchema = Type.Object({
  id: UuidSchema,
  name: Type.String(),
});
export type CommunicationDepartmentRef = Static<typeof CommunicationDepartmentRefSchema>;

/**
 * A single attachment row, returned from the API. Doc-only and comm-only
 * fields are nullable — the consumer switches on `kind`.
 */
export const AttachmentSchema = Type.Object({
  id: UuidSchema,
  kind: AttachmentKindSchema,

  // Anchor — exactly one kind populated
  projectId: Type.Union([UuidSchema, Type.Null()]),
  projectItemId: Type.Union([UuidSchema, Type.Null()]),
  orderBatchId: Type.Union([UuidSchema, Type.Null()]),
  bankGuaranteeId: Type.Union([UuidSchema, Type.Null()]),
  inspectionEventId: Type.Union([UuidSchema, Type.Null()]),
  // Patch 2 — master-entity anchors
  clientId: Type.Union([UuidSchema, Type.Null()]),
  supplierId: Type.Union([UuidSchema, Type.Null()]),
  masterDepartmentId: Type.Union([UuidSchema, Type.Null()]),
  // Patch 2 — denormalised traverse columns (read-only for cross-cutting filters)
  traverseClientId: Type.Union([UuidSchema, Type.Null()]),
  traverseSupplierId: Type.Union([UuidSchema, Type.Null()]),

  // Document-specific
  displayName: Type.Union([Type.String(), Type.Null()]),
  fileSizeBytes: Type.Union([Type.Integer(), Type.Null()]),
  fileMimeType: Type.Union([Type.String(), Type.Null()]),
  documentType: Type.Union([DocumentTypeSchema, Type.Null()]),
  documentDate: Type.Union([IsoDateSchema, Type.Null()]),
  supersedesId: Type.Union([UuidSchema, Type.Null()]),
  /**
   * Presigned download URL — short TTL (5 min). Only present for documents;
   * null for communications. The frontend re-fetches via GET /:id when the
   * URL expires.
   */
  downloadUrl: Type.Union([Type.String(), Type.Null()]),

  // Communication-specific
  commType: Type.Union([CommTypeSchema, Type.Null()]),
  direction: Type.Union([CommDirectionSchema, Type.Null()]),
  occurredAt: Type.Union([IsoDateTimeSchema, Type.Null()]),
  counterpartyName: Type.Union([Type.String(), Type.Null()]),
  counterpartyContactId: Type.Union([UuidSchema, Type.Null()]),
  referenceNumber: Type.Union([Type.String(), Type.Null()]),
  communicationDepartmentId: Type.Union([UuidSchema, Type.Null()]),
  communicationDepartmentName: Type.Union([Type.String(), Type.Null()]),
  copiedDepartments: Type.Array(CommunicationDepartmentRefSchema),
  subject: Type.Union([Type.String(), Type.Null()]),
  notes: Type.Union([Type.String(), Type.Null()]),
  // Patch 2 — threading
  threadId: Type.Union([UuidSchema, Type.Null()]),
  inReplyToId: Type.Union([UuidSchema, Type.Null()]),

  // Common
  description: Type.Union([Type.String(), Type.Null()]),
  tags: Type.Array(Type.String()),
  createdAt: IsoDateTimeSchema,
  createdByUserId: UuidSchema,
  createdByDisplayName: Type.String(),
  updatedAt: IsoDateTimeSchema,
  rowVersion: RowVersionSchema,
  // Patch 4 — soft-delete timestamp. Surfaced primarily for thread + supersede
  // rendering where deleted items remain in the response as placeholders.
  // Null for live attachments; ISO timestamp for deleted ones.
  deletedAt: Type.Union([IsoDateTimeSchema, Type.Null()]),
});
export type Attachment = Static<typeof AttachmentSchema>;

// ---------------------------------------------------------------------------
// Create — upload-url (step 1)
// ---------------------------------------------------------------------------

/**
 * Caller declares the file they intend to upload; server returns a presigned
 * PUT URL and a storage key. The browser then PUTs the file directly to
 * MinIO, then calls one of the commit endpoints with the storage key.
 */
export const AttachmentUploadUrlRequestSchema = Type.Object({
  filename: Type.String({ minLength: 1, maxLength: 255 }),
  mimeType: Type.String({ minLength: 1, maxLength: 200 }),
  sizeBytes: Type.Integer({ minimum: 1 }),
});
export type AttachmentUploadUrlRequest = Static<typeof AttachmentUploadUrlRequestSchema>;

export const AttachmentUploadUrlResponseSchema = Type.Object({
  uploadUrl: Type.String(),
  storageKey: Type.String(),
  expiresIn: Type.Integer({ minimum: 1 }),
});
export type AttachmentUploadUrlResponse = Static<typeof AttachmentUploadUrlResponseSchema>;

// ---------------------------------------------------------------------------
// Create — document commit (step 2)
// ---------------------------------------------------------------------------

export const DocumentCreateSchema = Type.Object({
  // Storage handle from the upload-url step
  storageKey: Type.String({ minLength: 1 }),
  fileSizeBytes: Type.Integer({ minimum: 1 }),
  fileMimeType: Type.String({ minLength: 1, maxLength: 200 }),

  // Metadata
  displayName: Type.String({ minLength: 1, maxLength: 255 }),
  documentType: DocumentTypeSchema,
  documentDate: IsoDateSchema,
  description: Type.Optional(Type.Union([Type.String({ maxLength: 4000 }), Type.Null()])),
  supersedesId: Type.Optional(Type.Union([UuidSchema, Type.Null()])),

  // Anchor
  anchor: AttachmentAnchorSchema,

  // Tags (free-text; will be normalised + upserted)
  tags: Type.Optional(Type.Array(Type.String())),
});
export type DocumentCreate = Static<typeof DocumentCreateSchema>;

// ---------------------------------------------------------------------------
// Create — communication
// ---------------------------------------------------------------------------

/**
 * A doc to attach to a new communication. The doc must already have been
 * uploaded to MinIO (separate upload-url call). The server creates the
 * attachment row for the doc as part of the same transaction that creates
 * the communication.
 */
export const CommunicationAttachedDocSchema = Type.Object({
  storageKey: Type.String({ minLength: 1 }),
  fileSizeBytes: Type.Integer({ minimum: 1 }),
  fileMimeType: Type.String({ minLength: 1, maxLength: 200 }),
  displayName: Type.String({ minLength: 1, maxLength: 255 }),
  documentType: DocumentTypeSchema,
  documentDate: IsoDateSchema,
});

export const CommunicationCreateSchema = Type.Object({
  commType: CommTypeSchema,
  direction: CommDirectionSchema,
  occurredAt: IsoDateTimeSchema,
  counterpartyName: Type.Optional(Type.Union([Type.String({ maxLength: 200 }), Type.Null()])),
  counterpartyContactId: Type.Optional(Type.Union([UuidSchema, Type.Null()])),
  referenceNumber: Type.Optional(Type.Union([Type.String({ maxLength: 200 }), Type.Null()])),
  communicationDepartmentId: Type.Optional(Type.Union([UuidSchema, Type.Null()])),
  copiedDepartmentIds: Type.Optional(Type.Array(UuidSchema, { maxItems: 50 })),
  subject: Type.String({ minLength: 1, maxLength: 500 }),
  notes: Type.Optional(Type.Union([Type.String({ maxLength: 10000 }), Type.Null()])),

  anchor: AttachmentAnchorSchema,
  tags: Type.Optional(Type.Array(Type.String())),

  // Patch 2 — threading. When set, this comm joins the thread of the
  // referenced parent. When null/omitted, a new thread is started with
  // thread_id = own id.
  inReplyToId: Type.Optional(Type.Union([UuidSchema, Type.Null()])),

  // Optional documents attached to this communication. Each becomes its own
  // attachment row with the same anchor + tags as the comm.
  attachedDocs: Type.Optional(Type.Array(CommunicationAttachedDocSchema)),
});
export type CommunicationCreate = Static<typeof CommunicationCreateSchema>;

// ---------------------------------------------------------------------------
// Update (communications only; documents are immutable except for supersede)
// ---------------------------------------------------------------------------

export const CommunicationUpdateSchema = Type.Object({
  subject: Type.Optional(Type.String({ minLength: 1, maxLength: 500 })),
  notes: Type.Optional(Type.Union([Type.String({ maxLength: 10000 }), Type.Null()])),
  description: Type.Optional(Type.Union([Type.String({ maxLength: 4000 }), Type.Null()])),
  occurredAt: Type.Optional(IsoDateTimeSchema),
  counterpartyName: Type.Optional(Type.Union([Type.String({ maxLength: 200 }), Type.Null()])),
  referenceNumber: Type.Optional(Type.Union([Type.String({ maxLength: 200 }), Type.Null()])),
  communicationDepartmentId: Type.Optional(Type.Union([UuidSchema, Type.Null()])),
  copiedDepartmentIds: Type.Optional(Type.Array(UuidSchema, { maxItems: 50 })),
  tags: Type.Optional(Type.Array(Type.String())),
  rowVersion: RowVersionSchema,
});
export type CommunicationUpdate = Static<typeof CommunicationUpdateSchema>;

// ---------------------------------------------------------------------------
// List query
// ---------------------------------------------------------------------------

export const AttachmentListQuerySchema = Type.Object({
  projectId: Type.Optional(UuidSchema),
  kind: Type.Optional(AttachmentKindSchema),

  // Sub-anchor filters (project tree)
  projectItemId: Type.Optional(UuidSchema),
  orderBatchId: Type.Optional(UuidSchema),
  bankGuaranteeId: Type.Optional(UuidSchema),
  inspectionEventId: Type.Optional(UuidSchema),
  /** "project-level only" — exclude any row with a sub-anchor. */
  projectLevelOnly: Type.Optional(Type.Boolean()),

  // Patch 2 — master anchor filters
  clientId: Type.Optional(UuidSchema),
  supplierId: Type.Optional(UuidSchema),
  masterDepartmentId: Type.Optional(UuidSchema),
  /** Anchor scope: 'PROJECT' (anything with project_id set) or 'MASTER' (direct master anchor). Used by cross-project page. */
  anchorScope: Type.Optional(
    Type.Union([Type.Literal('PROJECT'), Type.Literal('MASTER')]),
  ),

  // Filters
  documentType: Type.Optional(DocumentTypeSchema),
  commType: Type.Optional(CommTypeSchema),
  direction: Type.Optional(CommDirectionSchema),
  tag: Type.Optional(Type.String()),
  search: Type.Optional(Type.String({ maxLength: 200 })),
  // ISO date strings; inclusive
  dateFrom: Type.Optional(IsoDateSchema),
  dateTo: Type.Optional(IsoDateSchema),
  /** Only show records that have at least one attached document (filters via thread). */
  hasAttachment: Type.Optional(Type.Boolean()),
  /**
   * Patch 2 — group communications by thread (one row per thread, surfacing
   * the most recent message). Documents are unaffected. Defaults to false
   * (one row per record).
   */
  threadGroup: Type.Optional(Type.Boolean()),

  page: Type.Optional(Type.Integer({ minimum: 1, default: 1 })),
  pageSize: Type.Optional(Type.Integer({ minimum: 1, maximum: 200, default: 50 })),
  // Patch 5 — sortable columns on the communications cross-project page.
  // 'occurredAt' = COALESCE(occurred_at, created_at) [default]
  // 'subject' = alpha by subject
  // 'counterpartyName' = alpha by counterparty (nulls last)
  sortBy: Type.Optional(
    Type.Union([
      Type.Literal('occurredAt'),
      Type.Literal('subject'),
      Type.Literal('counterpartyName'),
    ]),
  ),
  sortDir: Type.Optional(Type.Union([Type.Literal('asc'), Type.Literal('desc')])),
});
export type AttachmentListQuery = Static<typeof AttachmentListQuerySchema>;

export const AttachmentListResponseSchema = Type.Object({
  items: Type.Array(AttachmentSchema),
  total: Type.Integer({ minimum: 0 }),
  page: Type.Integer({ minimum: 1 }),
  pageSize: Type.Integer({ minimum: 1 }),
});
export type AttachmentListResponse = Static<typeof AttachmentListResponseSchema>;

// ---------------------------------------------------------------------------
// Tag autocomplete
// ---------------------------------------------------------------------------

export const TagAutocompleteQuerySchema = Type.Object({
  q: Type.Optional(Type.String({ maxLength: 50 })),
});
export type TagAutocompleteQuery = Static<typeof TagAutocompleteQuerySchema>;

export const TagAutocompleteResponseSchema = Type.Object({
  tags: Type.Array(
    Type.Object({
      name: Type.String(),
      usageCount: Type.Integer({ minimum: 0 }),
    }),
  ),
});
export type TagAutocompleteResponse = Static<typeof TagAutocompleteResponseSchema>;

// ---------------------------------------------------------------------------
// Per-entity attachment counts (inline badge data)
//
// Returns a count per known sub-anchor across one project. The badge on each
// BG / part / batch / inspection-event reads its bucket from this single
// payload rather than firing one request per entity.
// ---------------------------------------------------------------------------

export const AttachmentCountsQuerySchema = Type.Object({
  projectId: Type.String({ format: 'uuid' }),
});
export type AttachmentCountsQuery = Static<typeof AttachmentCountsQuerySchema>;

export const AttachmentCountsResponseSchema = Type.Object({
  /** Attachments anchored to this project but no sub-entity. */
  projectLevel: Type.Integer({ minimum: 0 }),
  /** Map of project_item_id → count. Includes both PART and SUB_PART. */
  byPart: Type.Record(Type.String(), Type.Integer({ minimum: 0 })),
  /** Map of order_batch_id → count. */
  byBatch: Type.Record(Type.String(), Type.Integer({ minimum: 0 })),
  /** Map of bank_guarantee_id → count. */
  byBg: Type.Record(Type.String(), Type.Integer({ minimum: 0 })),
  /** Map of inspection_event_id → count. */
  byInspection: Type.Record(Type.String(), Type.Integer({ minimum: 0 })),
});
export type AttachmentCountsResponse = Static<typeof AttachmentCountsResponseSchema>;

// ---------------------------------------------------------------------------
// Patch 2 — Thread expansion
// ---------------------------------------------------------------------------

/**
 * Full thread, oldest-first. Each item includes its directly-attached
 * documents (also full Attachment rows), since the drawer needs to render
 * them inline below each message.
 */
export const ThreadResponseSchema = Type.Object({
  threadId: UuidSchema,
  /** Oldest-first ordering by occurred_at. */
  messages: Type.Array(
    Type.Object({
      message: AttachmentSchema,
      attachedDocs: Type.Array(AttachmentSchema),
    }),
  ),
});
export type ThreadResponse = Static<typeof ThreadResponseSchema>;

// ---------------------------------------------------------------------------
// Patch 2 — Last contact (drives Receivables / Forecast "Last contact …" lines)
// ---------------------------------------------------------------------------

export const LastContactQuerySchema = Type.Object({
  /** Comma-separated UUIDs. Server batches the lookup. */
  projectIds: Type.Optional(Type.String()),
  bankGuaranteeIds: Type.Optional(Type.String()),
});
export type LastContactQuery = Static<typeof LastContactQuerySchema>;

const LastContactSummarySchema = Type.Object({
  attachmentId: UuidSchema,
  threadId: UuidSchema,
  subject: Type.String(),
  occurredAt: IsoDateTimeSchema,
  direction: CommDirectionSchema,
  commType: CommTypeSchema,
  counterpartyName: Type.Union([Type.String(), Type.Null()]),
});

export const LastContactResponseSchema = Type.Object({
  /** project_id → most-recent comm anchored to that project (or any of its sub-anchors). */
  byProject: Type.Record(Type.String(), LastContactSummarySchema),
  /** bg_id → most-recent comm anchored to that BG specifically. */
  byBg: Type.Record(Type.String(), LastContactSummarySchema),
});
export type LastContactResponse = Static<typeof LastContactResponseSchema>;

// ---------------------------------------------------------------------------
// Patch 2 — Master entity attachments (for the master-detail Documents tabs)
// ---------------------------------------------------------------------------

/**
 * "Direct" attachments are those anchored to the master entity itself.
 * "Related" attachments are anchored within projects that involve this master
 * (via projects.client_id / batches.supplier_id / project_departments).
 */
export const MasterEntityAttachmentsQuerySchema = Type.Object({
  kind: Type.Union([
    Type.Literal('CLIENT'),
    Type.Literal('SUPPLIER'),
    Type.Literal('DEPARTMENT'),
  ]),
  id: UuidSchema,
});
export type MasterEntityAttachmentsQuery = Static<typeof MasterEntityAttachmentsQuerySchema>;

export const MasterEntityAttachmentsResponseSchema = Type.Object({
  direct: Type.Array(AttachmentSchema),
  related: Type.Array(AttachmentSchema),
});
export type MasterEntityAttachmentsResponse = Static<typeof MasterEntityAttachmentsResponseSchema>;

// ---------------------------------------------------------------------------
// Patch 3 — Supersede chain
// ---------------------------------------------------------------------------

/**
 * Versions of a document, ordered oldest → newest. The document the caller
 * passed in is included (so the consumer can highlight it in the timeline).
 *
 * Resolution: walk supersedes_id backward to the original, then walk
 * forward by finding rows whose supersedes_id matches each known id.
 * Bounded at MAX_CHAIN_DEPTH (10) in each direction to defend against
 * malformed data (the CREATE-time check already prevents self-reference,
 * but defensive bounds are cheap).
 *
 * Soft-deleted versions ARE included in the chain — the timeline reads
 * "version 1 (deleted) → version 2" — but they're flagged with `deletedAt`
 * so the UI can render the placeholder.
 */
export const SupersedeChainResponseSchema = Type.Object({
  /** Anchor doc the caller asked about. */
  anchorId: UuidSchema,
  /** Ordered oldest → newest. */
  versions: Type.Array(
    Type.Object({
      attachment: AttachmentSchema,
      /** True for the doc the caller passed in. */
      isAnchor: Type.Boolean(),
      /** True if soft-deleted. */
      isDeleted: Type.Boolean(),
    }),
  ),
  /** True if the chain hit the depth limit in either direction. */
  truncated: Type.Boolean(),
});
export type SupersedeChainResponse = Static<typeof SupersedeChainResponseSchema>;
